import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { BulkSelection, ContactListItem, CreateListInput, CreateSegmentInput, MergeSegmentsInput, CreateTagInput, ListItem, Paginated, RuleGroup, SegmentItem, TagItem } from '@crm/shared';
import { useFeatures } from '../settings/api';
import { api } from '../../api/client';
import type { ListParams } from '../../components/list-params';

export interface PipelineItem {
  id: string;
  name: string;
  isDefault: boolean;
  stages: { id: string; name: string; kind: 'OPEN' | 'WON' | 'LOST'; color: string | null; sortOrder: number }[];
}

// --- reads --------------------------------------------------------------------

export const useTags = () => useQuery({ queryKey: ['tags'], queryFn: async () => (await api.get<TagItem[]>('/tags')).data });
export const useLists = () => useQuery({ queryKey: ['lists'], queryFn: async () => (await api.get<ListItem[]>('/lists')).data });
export const useSegments = () => useQuery({ queryKey: ['segments'], queryFn: async () => (await api.get<SegmentItem[]>('/segments')).data });
export const usePipelines = (enabled = true) => useQuery({ queryKey: ['pipelines'], queryFn: async () => (await api.get<PipelineItem[]>('/pipelines')).data, staleTime: 5 * 60_000, enabled });

export function useList(id: string | undefined) {
  return useQuery({ queryKey: ['list', id], queryFn: async () => (await api.get<ListItem>(`/lists/${id}`)).data, enabled: !!id });
}
export function useSegment(id: string | undefined) {
  return useQuery({ queryKey: ['segment', id], queryFn: async () => (await api.get<SegmentItem>(`/segments/${id}`)).data, enabled: !!id });
}

export function useListContacts(id: string | undefined, params: ListParams) {
  return useQuery({
    queryKey: ['list-contacts', id, params],
    queryFn: async () => (await api.get<Paginated<ContactListItem>>(`/lists/${id}/contacts`, { params })).data,
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}
export function useSegmentContacts(id: string | undefined, params: ListParams) {
  return useQuery({
    queryKey: ['segment-contacts', id, params],
    queryFn: async () => (await api.get<Paginated<ContactListItem>>(`/segments/${id}/contacts`, { params })).data,
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

/** Everything the filter builder needs to offer tag/list/segment/deal conditions. */
export function useAudienceData() {
  const tags = useTags();
  const lists = useLists();
  const segments = useSegments();
  const pipelines = usePipelines(useFeatures().enabled('pipelines'));
  return { tags: (tags.data ?? []).map((t) => ({ ...t, name: t.path })), lists: lists.data ?? [], segments: segments.data ?? [], pipelines: pipelines.data ?? [] };
}

export function useSegmentPreview(rules: RuleGroup | undefined) {
  return useQuery({
    queryKey: ['segment-preview', rules],
    queryFn: async () => (await api.post<{ count: number }>('/segments/preview', { rules })).data,
    enabled: !!rules && rules.conditions.length > 0,
    placeholderData: keepPreviousData,
  });
}

// --- writes -------------------------------------------------------------------

function useInvalidate(keys: string[][]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}
const CONTACT_KEYS = [['contacts'], ['contact'], ['activities'], ['list-contacts'], ['segment-contacts'], ['organization-contacts']];

export function useCreateTag() {
  const inv = useInvalidate([['tags']]);
  return useMutation({ mutationFn: async (input: CreateTagInput) => (await api.post<TagItem>('/tags', input)).data, onSuccess: inv });
}
export function useUpdateTag() {
  const inv = useInvalidate([['tags'], ...CONTACT_KEYS]);
  return useMutation({ mutationFn: async ({ id, ...input }: Partial<CreateTagInput> & { id: string }) => (await api.patch<TagItem>(`/tags/${id}`, input)).data, onSuccess: inv });
}
export function useDeleteTag() {
  const inv = useInvalidate([['tags'], ...CONTACT_KEYS]);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/tags/${id}`)).data, onSuccess: inv });
}

export function useCreateList() {
  const inv = useInvalidate([['lists']]);
  return useMutation({ mutationFn: async (input: CreateListInput) => (await api.post<ListItem>('/lists', input)).data, onSuccess: inv });
}
export function useUpdateList() {
  const inv = useInvalidate([['lists'], ['list']]);
  return useMutation({ mutationFn: async ({ id, ...input }: Partial<CreateListInput> & { id: string }) => (await api.patch<ListItem>(`/lists/${id}`, input)).data, onSuccess: inv });
}
export function useDeleteList() {
  const inv = useInvalidate([['lists'], ...CONTACT_KEYS]);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/lists/${id}`)).data, onSuccess: inv });
}
export function useListMembers() {
  const inv = useInvalidate([['lists'], ['list'], ...CONTACT_KEYS]);
  return useMutation({
    mutationFn: async ({ listId, action, selection }: { listId: string; action: 'add' | 'remove'; selection: BulkSelection }) =>
      (await api.post<{ added?: number; removed?: number }>(`/lists/${listId}/members${action === 'remove' ? '/remove' : ''}`, { selection })).data,
    onSuccess: inv,
  });
}

export function useCreateSegment() {
  const inv = useInvalidate([['segments'], ...CONTACT_KEYS]);
  return useMutation({ mutationFn: async (input: CreateSegmentInput) => (await api.post<SegmentItem>('/segments', input)).data, onSuccess: inv });
}
export function useUpdateSegment() {
  const inv = useInvalidate([['segments'], ['segment'], ...CONTACT_KEYS]);
  return useMutation({ mutationFn: async ({ id, ...input }: Partial<CreateSegmentInput> & { id: string }) => (await api.patch<SegmentItem>(`/segments/${id}`, input)).data, onSuccess: inv });
}
export function useDeleteSegment() {
  const inv = useInvalidate([['segments'], ...CONTACT_KEYS]);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/segments/${id}`)).data, onSuccess: inv });
}
export function useMergeSegments() {
  const inv = useInvalidate([['segments'], ['segment'], ['campaigns'], ['campaign'], ...CONTACT_KEYS]);
  return useMutation({ mutationFn: async (input: MergeSegmentsInput) => (await api.post<SegmentItem & { dynamic: boolean }>('/segments/merge', input)).data, onSuccess: inv });
}
export function useRefreshSegment() {
  const inv = useInvalidate([['segments'], ['segment'], ...CONTACT_KEYS]);
  return useMutation({
    mutationFn: async (id: string) => (await api.post<{ memberCount: number; entered: number; left: number }>(`/segments/${id}/refresh`)).data,
    onSuccess: inv,
  });
}

// --- bulk ---------------------------------------------------------------------

export function useBulkTags() {
  const inv = useInvalidate([['tags'], ...CONTACT_KEYS]);
  return useMutation({
    mutationFn: async (input: { selection: BulkSelection; add?: string[]; remove?: string[] }) =>
      (await api.post<{ contacts: number; added: number; removed: number }>('/contacts/bulk/tags', input)).data,
    onSuccess: inv,
  });
}
export function useBulkLists() {
  const inv = useInvalidate([['lists'], ...CONTACT_KEYS]);
  return useMutation({
    mutationFn: async (input: { selection: BulkSelection; add?: string[]; remove?: string[] }) =>
      (await api.post<{ contacts: number; added: number; removed: number }>('/contacts/bulk/lists', input)).data,
    onSuccess: inv,
  });
}
export function useBulkSegments() {
  const inv = useInvalidate([['segments'], ['segment'], ...CONTACT_KEYS]);
  return useMutation({
    mutationFn: async (input: { selection: BulkSelection; add?: string[]; remove?: string[] }) =>
      (await api.post<{ contacts: number; added: number; removed: number }>('/contacts/bulk/segments', input)).data,
    onSuccess: inv,
  });
}
export function useBulkUpdate() {
  const inv = useInvalidate(CONTACT_KEYS);
  return useMutation({
    mutationFn: async (input: { selection: BulkSelection; data: Record<string, unknown> }) => (await api.post<{ updated: number }>('/contacts/bulk/update', input)).data,
    onSuccess: inv,
  });
}
export function useBulkDelete() {
  const inv = useInvalidate([['dashboard'], ['lists'], ['segments'], ...CONTACT_KEYS]);
  return useMutation({
    mutationFn: async (selection: BulkSelection) => (await api.post<{ deleted: number }>('/contacts/bulk/delete', { selection })).data,
    onSuccess: inv,
  });
}
export function useSetContactTags() {
  const inv = useInvalidate([['tags'], ...CONTACT_KEYS]);
  return useMutation({
    mutationFn: async ({ id, tagIds }: { id: string; tagIds: string[] }) => (await api.put<TagItem[]>(`/contacts/${id}/tags`, { tagIds })).data,
    onSuccess: inv,
  });
}
